import React, { useState, useEffect } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { 
  Table, 
  Form, 
  Input, 
  Select, 
  Button, 
  Card, 
  Typography, 
  message, 
  Spin,
  Radio,
  Space,
  Row,
  Col,
  Divider
} from 'antd';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;

interface Usuario {
  id: number;
  apellidoynombre: string;
  email: string;
  empresa: string;
  cuit_empresa: string;
  autorizado: boolean;
  id_empresa: number;
}

interface Cliente {
  id_padron: number;
  nc: string;
  denominacion: string;
  // Agrega otros campos según la respuesta de la API
}

const AutorizacionUsuarios: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [form] = Form.useForm();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUsuario, setSelectedUsuario] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedActividad, setSelectedActividad] = useState<string>('');
  const [selectedPadron, setSelectedPadron] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const itemsPerPage = 5;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const token = await getAccessTokenSilently();
        
        // Cargar usuarios
        const usuariosUrl = `${process.env.REACT_APP_USUARIOS_URL}/leer_usuarios/${user?.id_empresa}`;
        const usuariosResponse = await fetch(usuariosUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!usuariosResponse.ok) {
          throw new Error('Error al cargar los usuarios');
        }

        const usuariosData = await usuariosResponse.json();
        setUsuarios(usuariosData);

        // Cargar padrón
        const padronUrl = `${process.env.REACT_APP_PADRON_URL}/leer_padron/${user?.id_empresa}/`;
        const padronResponse = await fetch(padronUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!padronResponse.ok) {
          throw new Error('Error al cargar el padrón');
        }

        const padronData = await padronResponse.json();
        setClientes(padronData);
      } catch (error) {
        console.error('Error:', error);
        setError('Error al cargar los datos. Por favor, intente nuevamente.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently, user]);

  const columns: ColumnsType<Usuario> = [
    {
      title: 'Seleccionar',
      key: 'seleccionar',
      render: (_, record) => (
        <Radio
          checked={selectedUsuario === record.id}
          onChange={() => !record.autorizado && setSelectedUsuario(record.id)}
          disabled={record.autorizado}
        />
      ),
    },
    {
      title: 'Apellido y Nombre',
      dataIndex: 'apellidoynombre',
      key: 'apellidoynombre',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Empresa',
      dataIndex: 'empresa',
      key: 'empresa',
    },
    {
      title: 'CUIT',
      dataIndex: 'cuit_empresa',
      key: 'cuit_empresa',
    },
    {
      title: 'Estado',
      key: 'estado',
      render: (_, record) => (
        <span>{record.autorizado ? 'Autorizado' : 'Pendiente'}</span>
      ),
    },
  ];

  const handleAutorizar = async (usuarioId: number) => {
    try {
      const values = await form.validateFields();
      const actividadPrimeraLetra = values.actividad.charAt(0).toUpperCase();
      const esOperador = actividadPrimeraLetra === 'O';

      if (!values.actividad || (!esOperador && !values.padron)) {
        message.error('Debe seleccionar una actividad y un cliente del padrón');
        return;
      }

      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const url = `${process.env.REACT_APP_USUARIOS_URL}/autorizar_usuarios/${usuarioId}/`;

      const clienteSeleccionado = !esOperador
        ? clientes.find(c => `${c.nc} - ${c.denominacion}` === values.padron)
        : null;

      if (!esOperador && !clienteSeleccionado) {
        message.error('Debe seleccionar un cliente válido del padrón');
        return;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          actividad: actividadPrimeraLetra,
          id_empresa: user?.id_empresa,
          ...(clienteSeleccionado ? {
            id_padron: clienteSeleccionado.id_padron,
            nc: clienteSeleccionado.nc,
            denominacion: clienteSeleccionado.denominacion
          } : {})
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Error al autorizar el usuario');
      }

      message.success('Usuario autorizado exitosamente');
      
      setUsuarios(prevUsuarios => 
        prevUsuarios.map(usuario => 
          usuario.id === usuarioId 
            ? { ...usuario, autorizado: true }
            : usuario
        )
      );

      form.resetFields();
      setSelectedUsuario(null);
      setSelectedActividad('');
      setSelectedPadron(null);

      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Error:', error);
      message.error(error instanceof Error ? error.message : 'Error al autorizar el usuario');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredClientes = clientes.filter(cliente => 
    cliente.nc.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.denominacion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const padronOptions = filteredClientes.map(cliente => ({
    label: `${cliente.nc} - ${cliente.denominacion}`,
    value: `${cliente.nc} - ${cliente.denominacion}`
  }));

  return (
    <>  
      <Divider orientation="left">
        <h1 style={{color:'#023059'}}>Autorización Usuarios</h1>
      </Divider>
    <div style={{ maxWidth: '80%', margin: '0 auto' }}>
      <Spin spinning={isLoading}>
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={usuarios}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: itemsPerPage,
            total: usuarios.length,
            onChange: (page) => {
              setCurrentPage(page);
              setSelectedUsuario(null);
            },
            showSizeChanger: false
          }}
          onRow={(record) => ({
            onClick: () => !record.autorizado && setSelectedUsuario(record.id),
            style: { cursor: record.autorizado ? 'default' : 'pointer' }
          })}
        />

        <Card 
          title={<span style={{ color: "white" }}>Datos del Usuario</span>}
          style={{ marginTop: '2rem', maxWidth: '80%', marginLeft: '10%' }}
        >
          {selectedUsuario ? (
            <Form
              form={form}
              layout="vertical"
              onFinish={() => handleAutorizar(selectedUsuario)}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item label="Apellido y Nombre">
                    <Input
                      value={usuarios.find(u => u.id === selectedUsuario)?.apellidoynombre}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item label="Email">
                    <Input
                      value={usuarios.find(u => u.id === selectedUsuario)?.email}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item label="Empresa">
                    <Input
                      value={usuarios.find(u => u.id === selectedUsuario)?.empresa}
                      disabled
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label="CUIT">
                    <Input
                      value={usuarios.find(u => u.id === selectedUsuario)?.cuit_empresa}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item
                    name="actividad"
                    label="Actividad"
                    rules={[{ required: true, message: 'Por favor seleccione una actividad' }]}
                  >
                    <Select
                      placeholder="Seleccione una actividad"
                      onChange={(value) => setSelectedActividad(value)}
                    >
                      <Select.Option value="desmotadora">Desmotadora</Select.Option>
                      <Select.Option value="operador">Operador de Desmote</Select.Option>
                      <Select.Option value="productor">Productor/Cliente</Select.Option>
                      <Select.Option value="transportista">Transportista</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="padron"
                    label="Padrón"
                    rules={[{ required: selectedActividad.charAt(0).toUpperCase() !== 'O', message: 'Por favor seleccione un cliente del padrón' }]}
                  >
                    <Select
                      showSearch
                      placeholder="Buscar por NC o denominación..."
                      options={padronOptions}
                      filterOption={false}
                      onSearch={(value) => setSearchTerm(value)}
                      onChange={(value) => {
                        setSelectedPadron(clientes.find(c => `${c.nc} - ${c.denominacion}` === value)?.id_padron || null);
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Space style={{ width: '100%', justifyContent: 'center' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    disabled={!selectedUsuario || !selectedActividad || (selectedActividad.charAt(0).toUpperCase() !== 'O' && !selectedPadron)}
                  >
                    Autorizar
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              Seleccione un usuario para ver sus datos
            </div>
          )}
        </Card>
      </Spin>
    </div>
    </>
  );
};

export default withAuthenticationRequired(AutorizacionUsuarios);
